/**
 * Curriculum Builder Page
 * AI-powered curriculum generation and management
 * 
 * Refactored: Split into smaller sub-components for maintainability
 * - CurriculumList: List of user's curriculums
 * - CurriculumDetail: Selected curriculum view with modules
 * - CreateCurriculumDialog: Form for creating new curriculum
 * - TopicDetailDialog: Detailed topic view with tabs
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Plus, BookOpen, Sparkles } from 'lucide-react';

// Import sub-components
import {
    CurriculumList,
    CurriculumDetail,
    CreateCurriculumDialog,
    TopicDetailDialog
} from '../components/curriculum';

const CurriculumBuilder = () => {
    const { api } = useAuth();
    
    // Main state
    const [curriculums, setCurriculums] = useState([]);
    const [selectedCurriculum, setSelectedCurriculum] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Dialog state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [creating, setCreating] = useState(false);
    const [topicDetailOpen, setTopicDetailOpen] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);
    
    // File upload state
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);

    // Fetch curriculums
    const fetchCurriculums = useCallback(async () => {
        try {
            const response = await api.get('/curriculum');
            setCurriculums(response.data);
            
            // Update selected curriculum if it exists
            if (selectedCurriculum) {
                const updated = response.data.find(c => c.id === selectedCurriculum.id);
                if (updated) setSelectedCurriculum(updated);
            }
        } catch (error) {
            console.error('Failed to fetch curriculums:', error);
            toast.error('Failed to load curriculums');
        } finally {
            setLoading(false);
        }
    }, [api, selectedCurriculum]);

    useEffect(() => {
        fetchCurriculums();
    }, [fetchCurriculums]);
    
    // Separate effect for polling
    useEffect(() => {
        // Poll for updates when generating
        const interval = setInterval(() => {
            const hasGenerating = curriculums.some(c => 
                c.generation_status === 'generating' || c.generation_status === 'regenerating'
            );
            if (hasGenerating) {
                fetchCurriculums();
            }
        }, 3000);
        
        return () => clearInterval(interval);
    }, [curriculums, fetchCurriculums]);

    // Status color helper
    const getStatusColor = (status) => {
        switch (status) {
            case 'draft': return 'bg-slate-500/10 text-slate-600';
            case 'generated': return 'bg-blue-500/10 text-blue-600';
            case 'pending_review': return 'bg-amber-500/10 text-amber-600';
            case 'approved': return 'bg-emerald-500/10 text-emerald-600';
            case 'published': return 'bg-emerald-500/10 text-emerald-600';
            default: return 'bg-slate-500/10 text-slate-600';
        }
    };

    // Create curriculum
    const handleCreate = async (formData) => {
        if (!formData.title.trim() || !formData.description.trim() || !formData.target_audience.trim()) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        setCreating(true);
        try {
            const response = await api.post('/curriculum', {
                ...formData,
                learning_goals: formData.learning_goals.filter(g => g.trim())
            });
            
            // Upload files if any
            if (uploadedFiles.length > 0) {
                for (const file of uploadedFiles) {
                    if (file.file) {
                        const formData = new FormData();
                        formData.append('file', file.file);
                        await api.post(`/curriculum/${response.data.id}/documents`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });
                    }
                }
            }
            
            toast.success('Curriculum created successfully');
            setShowCreateDialog(false);
            setUploadedFiles([]);
            fetchCurriculums();
            setSelectedCurriculum(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create curriculum');
        } finally {
            setCreating(false);
        }
    };

    // Generate curriculum content
    const generateCurriculum = async (curriculumId) => {
        try {
            await api.post(`/curriculum/${curriculumId}/generate`);
            toast.success('Generation started');
            fetchCurriculums();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start generation');
        }
    };

    // Regenerate content
    const regenerateContent = async (curriculumId) => {
        try {
            await api.post(`/curriculum/${curriculumId}/regenerate`);
            toast.success('Regeneration started');
            fetchCurriculums();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to regenerate');
        }
    };

    // Publish curriculum
    const publishCurriculum = async (curriculumId) => {
        try {
            await api.post(`/curriculum/${curriculumId}/publish`);
            toast.success('Curriculum published');
            fetchCurriculums();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to publish');
        }
    };

    // Delete curriculum
    const deleteCurriculum = async (curriculumId) => {
        if (!window.confirm('Are you sure you want to delete this curriculum?')) return;
        
        try {
            await api.delete(`/curriculum/${curriculumId}`);
            toast.success('Curriculum deleted');
            if (selectedCurriculum?.id === curriculumId) {
                setSelectedCurriculum(null);
            }
            fetchCurriculums();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete');
        }
    };

    // File upload handler
    const handleFileUpload = async (e, curriculumId) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/curriculum/${curriculumId}/documents`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Document uploaded');
            fetchCurriculums();
        } catch (error) {
            toast.error('Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    // View topic detail
    const viewTopicDetail = async (curriculumId, topicId) => {
        try {
            const response = await api.get(`/curriculum/${curriculumId}/topics/${topicId}`);
            setSelectedTopic({
                curriculum_id: curriculumId,
                topic: response.data
            });
            setTopicDetailOpen(true);
        } catch (error) {
            toast.error('Failed to load topic details');
        }
    };

    // Submit feedback for topic
    const submitFeedback = async (feedbackText, regenerateSections) => {
        if (!selectedTopic || !feedbackText.trim()) {
            toast.error('Please enter feedback');
            return;
        }
        
        try {
            await api.post(`/curriculum/${selectedTopic.curriculum_id}/topics/${selectedTopic.topic.id}/feedback`, {
                feedback: feedbackText,
                regenerate_sections: regenerateSections
            });
            toast.success('Feedback submitted');
            fetchCurriculums();
            setTopicDetailOpen(false);
        } catch (error) {
            toast.error('Failed to submit feedback');
        }
    };

    // Approve topic
    const approveTopic = async () => {
        if (!selectedTopic) return;
        
        try {
            await api.post(`/curriculum/${selectedTopic.curriculum_id}/topics/${selectedTopic.topic.id}/approve`);
            toast.success('Topic approved');
            fetchCurriculums();
            setTopicDetailOpen(false);
        } catch (error) {
            toast.error('Failed to approve topic');
        }
    };

    return (
        <MainLayout>
            <div className="p-6 space-y-6" data-testid="curriculum-builder-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <BookOpen className="h-6 w-6 text-primary" />
                            Curriculum Builder
                        </h1>
                        <p className="text-muted-foreground">
                            Create and manage AI-powered training curriculums
                        </p>
                    </div>
                    <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-new-curriculum-btn">
                        <Plus className="h-4 w-4" />
                        Create Curriculum
                    </Button>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Curriculum List */}
                    <div className="lg:col-span-1">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Sparkles className="h-4 w-4 text-yellow-500" />
                                    My Curriculums
                                </CardTitle>
                                <CardDescription>
                                    {curriculums.length} curriculum{curriculums.length !== 1 ? 's' : ''}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <CurriculumList
                                    curriculums={curriculums}
                                    loading={loading}
                                    selectedId={selectedCurriculum?.id}
                                    onSelect={setSelectedCurriculum}
                                    onCreateNew={() => setShowCreateDialog(true)}
                                    getStatusColor={getStatusColor}
                                />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right: Curriculum Detail */}
                    <div className="lg:col-span-2">
                        <CurriculumDetail
                            curriculum={selectedCurriculum}
                            onGenerate={generateCurriculum}
                            onRegenerate={regenerateContent}
                            onPublish={publishCurriculum}
                            onDelete={deleteCurriculum}
                            onFileUpload={handleFileUpload}
                            onViewTopic={viewTopicDetail}
                            getStatusColor={getStatusColor}
                        />
                    </div>
                </div>

                {/* Create Dialog */}
                <CreateCurriculumDialog
                    open={showCreateDialog}
                    onOpenChange={setShowCreateDialog}
                    onSubmit={handleCreate}
                    creating={creating}
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                    uploading={uploading}
                />

                {/* Topic Detail Dialog */}
                <TopicDetailDialog
                    open={topicDetailOpen}
                    onOpenChange={setTopicDetailOpen}
                    topicData={selectedTopic}
                    onSubmitFeedback={submitFeedback}
                    onApprove={approveTopic}
                />
            </div>
        </MainLayout>
    );
};

export default CurriculumBuilder;
